import axios from './axiosConfig';

export interface Location {
  location: string;
  location_address: string;
}

export interface LocationResponse {
  locations: Location[];
  total: number;
}

export interface Botel {
  id: string;
  name: string;
  description: string;
  location: string;
  location_address: string;
  images: string[];
  device_status: string;
  host_partner_id: string;
  amenities: Record<string, any>;
  is_available: boolean;
  cleaning_fee?: number;
  service_fee?: number;
  minimum_stay?: number;
  maximum_stay?: number;
  device_serial?: string;
  latitude?: number;
  longitude?: number;
  remote_lock_id?: string;
  installation_date?: Date;
  // New unified system fields
  mode: 'STR' | 'DELIVERY' | 'HYBRID';
  carrier_sla_enabled: boolean;
  compartments?: Compartment[];
  created_at: Date;
  updated_at: Date;
  host_partner?: {
    id: string;
    name: string;
    email: string;
    company_name?: string;
  };
}

export interface Compartment {
  id: string;
  botel_id: string;
  name: string;
  description?: string;
  comp_type: 'CABIN' | 'SUITE';
  occupancy_mode: 'SINGLE' | 'MULTI';
  price_per_hour?: number;
  storage_capacity: number;
  amenities?: Record<string, any>;
  is_active: boolean;
  lock_id?: string;
  access_code?: string;
  capacity?: CompartmentCapacity;
  created_at: Date;
  updated_at: Date;
}

export interface CompartmentCapacity {
  compartment_id: string;
  max_units: number;
}

export interface PackageItem {
  id: string;
  allocation_id: string;
  botel_id: string;
  compartment_id: string;
  carrier?: string;
  tracking_code?: string;
  size_code?: string;
  state: 'expected' | 'deposited' | 'picked_up' | 'missing';
  deposit_code_hash?: string;
  claim_code_hash?: string;
  recipient_name?: string;
  recipient_phone?: string;
  description?: string;
  metadata?: Record<string, any>;
  created_at: Date;
  updated_at: Date;
}

export interface BotelResponse {
  botels: Botel[];
  total: number;
}

export interface PaginatedBotelResponse {
  totalItems: number;
  data: Botel[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

export interface CreateBotelRequest {
  host_partner_id: string;
  name: string;
  description: string;
  location: string;
  amenities?: Record<string, any>;
  images?: string[];
  cleaning_fee?: number;
  service_fee?: number;
  minimum_stay?: number;
  maximum_stay?: number;
  device_serial?: string;
  location_address?: string;
  latitude?: number;
  longitude?: number;
  remote_lock_id?: string;
  installation_date?: Date;
  device_status?: string;
  // New unified system fields
  mode?: 'STR' | 'DELIVERY' | 'HYBRID';
  carrier_sla_enabled?: boolean;
}

export interface CreateCompartmentRequest {
  name: string;
  description?: string;
  price_per_hour?: number;
  storage_capacity?: number;
  amenities?: Record<string, any>;
}

export interface BotelFilters {
  search?: string;
  status?: string;
  minPrice?: number;
  maxPrice?: number;
  minCapacity?: number;
  maxCapacity?: number;
  hostPartnerId?: string;
}

export const botelService = {
  // CRUD operations
  getAllBotels: async (
    page: number = 1, 
    size: number = 10, 
    sort: string = 'created_at', 
    sortOrder: 'ASC' | 'DESC' = 'DESC', 
    search?: string,
    filters?: BotelFilters
  ): Promise<PaginatedBotelResponse> => {
    try {
      const params: any = { 
        page, 
        size,
        sort,
        sortOrder
      };
      
      // Add search parameter if provided
      if (search) {
        params.search = search;
      }
      
      // Add filter parameters
      if (filters) {
        if (filters.status) params.status = filters.status;
        if (filters.minPrice !== undefined) params.minPrice = filters.minPrice;
        if (filters.maxPrice !== undefined) params.maxPrice = filters.maxPrice;
        if (filters.minCapacity !== undefined) params.minCapacity = filters.minCapacity;
        if (filters.maxCapacity !== undefined) params.maxCapacity = filters.maxCapacity;
        if (filters.hostPartnerId) params.hostPartnerId = filters.hostPartnerId;
      }

      const response = await axios.get('botels', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch botels:', error);
      return { totalItems: 0, data: [], currentPage: 1, totalPages: 0, pageSize: 10 };
    }
  },

  getBotelById: async (id: string): Promise<Botel> => {
    try {
      const response = await axios.get(`botels/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch botel:', error);
      throw error;
    }
  },

  createBotel: async (botelData: CreateBotelRequest): Promise<Botel> => {
    try {
      const response = await axios.post('botels', botelData);
      return response.data;
    } catch (error) {
      console.error('Failed to create botel:', error);
      throw error;
    }
  },

  updateBotel: async (id: string, botelData: Partial<CreateBotelRequest>): Promise<Botel> => {
    try {
      const response = await axios.put(`botels/${id}`, botelData);
      return response.data;
    } catch (error) {
      console.error('Failed to update botel:', error);
      throw error;
    }
  },

  deleteBotel: async (id: string): Promise<void> => {
    try {
      await axios.delete(`botels/${id}`);
    } catch (error) {
      console.error('Failed to delete botel:', error);
      throw error;
    }
  },

  // Search operations
  searchLocations: async (query?: string, page: number = 1, limit: number = 10): Promise<LocationResponse> => {
    try {
      const response = await axios.get('botels/locations/search', {
        params: { query, page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search locations:', error);
      return { locations: [], total: 0 };
    }
  },

  searchBotels: async (location: string, date?: string, duration?: string): Promise<BotelResponse> => {
    try {
      const response = await axios.get('botels/search', {
        params: { location, date, duration }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search botels:', error);
      return { botels: [], total: 0 };
    }
  },

  // === New Unified System Functions ===
  
  // Mode management
  setBotelMode: async (id: string, mode: 'STR' | 'DELIVERY' | 'HYBRID'): Promise<Botel> => {
    try {
      const response = await axios.patch(`botels-v2/${id}/mode`, { mode });
      return response.data;
    } catch (error) {
      console.error('Failed to set botel mode:', error);
      throw error;
    }
  },

  // Get botel with compartments
  getBotelWithCompartments: async (id: string): Promise<Botel> => {
    try {
      const response = await axios.get(`botels-v2/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch botel with compartments:', error);
      throw error;
    }
  },

  // Compartment management
  getCompartments: async (botelId: string): Promise<Compartment[]> => {
    try {
      const response = await axios.get(`botels-v2/${botelId}/compartments`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch compartments:', error);
      return [];
    }
  },

  createCompartment: async (botelId: string, compartmentData: CreateCompartmentRequest): Promise<Compartment> => {
    try {
      const response = await axios.post(`botels-v2/${botelId}/compartments`, compartmentData);
      return response.data;
    } catch (error) {
      console.error('Failed to create compartment:', error);
      throw error;
    }
  },

  // Location management
  updateBotelLocation: async (id: string, latitude: number, longitude: number, locationAddress?: string): Promise<void> => {
    try {
      await axios.patch(`botels-v2/${id}/location`, { 
        latitude, 
        longitude, 
        location_address: locationAddress 
      });
    } catch (error) {
      console.error('Failed to update botel location:', error);
      throw error;
    }
  },

  // Search nearby botels
  searchNearbyBotels: async (lat: number, lng: number, radius?: number, mode?: string): Promise<Botel[]> => {
    try {
      const response = await axios.get('botels-v2/search/nearby', {
        params: { lat, lng, radius, mode }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search nearby botels:', error);
      return [];
    }
  },

  // Revenue
  getRevenue: async (botelId: string, startDate?: string, endDate?: string): Promise<any> => {
    try {
      const response = await axios.get(`botels-v2/${botelId}/revenue`, {
        params: { start_date: startDate, end_date: endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch revenue:', error);
      throw error;
    }
  },

  postRevenue: async (botelId: string, revenueData: {
    source: string;
    host_id: string;
    gross_cents: number;
    source_ref?: string;
    description?: string;
  }): Promise<void> => {
    try {
      await axios.post(`botels-v2/${botelId}/revenue`, revenueData);
    } catch (error) {
      console.error('Failed to post revenue:', error);
      throw error;
    }
  }
}; 